import { EditableItem, SupportedParagraphStyle, editableId } from '../constants';
import { colorMarkClassName } from '../../../constants';
import * as helpers from '../../helpers';
import { TextFieldsEditableData, addStylesToEditableTextFieldsData } from '../../editableTextFields/textFieldsEditableData';

export interface HtmlTextFieldsConfig {
	page: number;
	id: string;
	content: string;
	width?: string | null;
	height?: string | null;
	isEditedFromBackoffice?: boolean;
}

export interface TextFieldsData {
	items: HtmlTextFieldsConfig[];
	editableData: TextFieldsEditableData | undefined;
	isError: boolean;
}

const getTextElementsFromStyles = (styles: string, innerHTML: string, className: string): HTMLSpanElement[] => {
	const stylesArray = helpers.getStylesArrayWithoutSpaces(styles);

	// text-decoration style is needed for proper work of <RichTextEditor> component
	if (!styles.includes('text-decoration')) {
		stylesArray.push('text-decoration:none');
	}

	const spanElements = stylesArray.map((style, index, array) => {
		const span = document.createElement('span');

		// A special class is needed for proper color of underline (managed in css)
		if (style.includes('color')) {
			span.classList.add(colorMarkClassName);
		}

		span.setAttribute('style', style);
		const isLast = index === array.length - 1;
		if (isLast) {
			span.innerHTML = innerHTML;
		}
		return span;
	});

	if (className) {
		const spanWithClassName = document.createElement('span');
		spanWithClassName.className = className;
		spanElements.unshift(spanWithClassName);
	}

	return spanElements;
};

const replaceTextElements = (oldElement: HTMLSpanElement, newElements: HTMLSpanElement[]): void => {
	for (let i = 1; i < newElements.length; i += 1) {
		newElements[i - 1].appendChild(newElements[i]);
	}

	oldElement.parentNode!.replaceChild(newElements[0], oldElement);
};

const setTextElementsContent = (elements: HTMLSpanElement[], editableData: TextFieldsEditableData): void => {
	elements.forEach((element) => {
		const styles = element.getAttribute('style');
		const className = element.getAttribute('class');
		const customClassName = className
			? className.replace(colorMarkClassName, '').trim()
			: '';

		if (styles) {
			// By default Slate adds line-break symbol to each paragraph of external pasted text. It should be removed for proper display
			const innerHTML = element.innerHTML.replace(/\n/g, '');
			const textElements = getTextElementsFromStyles(styles, innerHTML, customClassName);
			replaceTextElements(element, textElements);
			addStylesToEditableTextFieldsData(editableData, styles);
		}
	});
};

const replaceBrTagsToEmptyParagraphs = (element: HTMLElement): void => {
	const brElements = [...element.querySelectorAll('br')];
	// do not change <br> inside paragraphs to keep structure of default html
	const filteredBrElements = brElements.filter(element => (
		element.parentElement && element.parentElement.tagName !== 'P')
	);

	filteredBrElements.forEach((element) => {
		const paragraph = document.createElement('p');
		element.parentNode!.replaceChild(paragraph, element);
	});
};

const setHeadingOrParagraphStyles = (element: HTMLElement, editableData: TextFieldsEditableData): void => {
	const stylesString = element.getAttribute('style');

	if (stylesString) {
		addStylesToEditableTextFieldsData(editableData, stylesString);
	}

	const editableElements = [...element.querySelectorAll('h1, h2, h3, h4, h5, h6, p')] as HTMLElement[];

	Object.values(SupportedParagraphStyle).forEach((styleName) => {
		const styleValue = element.style[styleName];

		editableElements.forEach((editableElement) => {
			if (!editableElement.style[styleName]) {
				if (styleValue) {
					editableElement.style[styleName] = styleValue;
				}
			} else {
				// add individual line-height of heading or paragraph to editable data
				const stylesString = editableElement.getAttribute('style');
				if (stylesString && stylesString.includes('line-height')) {
					const lineHeightStyleString = stylesString
						.split(';')
						.filter(style => style.includes('line-height'))
						.join();
					addStylesToEditableTextFieldsData(editableData, lineHeightStyleString);
				}
			}
		});
	});
};

const trimInnerHtml = (element: HTMLElement): void => {
	for (let i = 0; i < element.childNodes.length; i++) {
		const childNode = element.childNodes[i] as HTMLElement;
		if (childNode.innerHTML) {
			childNode.innerHTML = childNode.innerHTML.trim();
			trimInnerHtml(childNode);
		}
	}
};

const getContent = (
	element: HTMLElement,
	editableData: TextFieldsEditableData,
	isModifiedFromBackoffice: boolean,
): string => {
	replaceBrTagsToEmptyParagraphs(element);
	setHeadingOrParagraphStyles(element, editableData);

	if (!isModifiedFromBackoffice) {
		// to remove unnecessary spaces/tabs from initial html (otherwise they will be kept in RTE)
		trimInnerHtml(element);
	}

	const textElements = [
		...element.querySelectorAll('span'),
	] as HTMLSpanElement[];

	setTextElementsContent(textElements, editableData);

	// We have to remove unnecessary spaces between tags for proper render in RTE
	return helpers.getStringWithoutSpaces(element.innerHTML);
};

const getConfig = (
	element: HTMLElement,
	page: number,
	editableData: TextFieldsEditableData,
	isModifiedFromBackoffice: boolean,
): HtmlTextFieldsConfig => {
	const id = element.getAttribute(editableId) as string;
	const { style: { width, height } } = element;

	return {
		page,
		id,
		width,
		height,
		content: getContent(element, editableData, isModifiedFromBackoffice),
	};
};

export default (page: number, htmlDocument: HTMLDocument, isModifiedFromBackoffice: boolean): TextFieldsData => {
	const items: HtmlTextFieldsConfig[] = [];
	const editableData = {
		fontFamilies: new Set<string>(),
		fontSizes: new Set<string>(),
		colors: new Set<string>(),
		lineHeights: new Set<string>(),
		letterSpacings: new Set<string>(),
	};
	const editableTextFieldElements = [
		...htmlDocument.querySelectorAll(`[${EditableItem.Text}]`),
	] as HTMLElement[];
	let isError: boolean = false;

	try {
		editableTextFieldElements.forEach(fieldElement => (
			items.push(getConfig(fieldElement, page, editableData, isModifiedFromBackoffice))
		));
	} catch (err) {
		isError = true;
	}

	return {
		items,
		editableData,
		isError,
	};
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/htmlEditor/editableTextFields/getData.ts